import { useEffect, useRef, useState } from "react";
import { ReportsListObj, ReportsListRes } from "../../../redux-store/types/api-types";
import { Form, Formik, FormikProps } from "../../../lib/formik/Formik";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import { RootStateType } from "../../../redux-store/reducers";
import { isModuleAllowed, maskInfo } from "../../../utils/utilityFunctions";
import { CONTENT_TYPE, MPOWER_ACCOUNT_TYPE_MASTER, REPORTS_DOWNLOAD_OPTION, USER_ROLES } from "../../../utils/constant";
import { getCorporateActionReport, getReportsAnalystInvestorList, getReportsInvestorList } from "../../../redux-store/actions/reports";
import dayjs from "../../../utils/dayjs";
import * as yup from "yup"
import { reportDownload } from "../../Reports/report-utils";
import { ReportsRightLayout } from "../../InvestorReport";
import { Box, Dialog, Grid, IconButton, Skeleton, Theme, Typography } from "@mui/material";
import FormikSearchDropdown from "../../../lib/formik/FormikSearchDropdown";
import { DayJsDatePicker } from "../../onboarding/newDatePicker";
import MFSelectField from "../../../lib/formik/SelectField";
import { MFSubmitButton } from "../../../lib/formik";
import { DialogCloseIcon, YellowWarning } from "../../../assets/SvgIcons";

interface Values {
    selectedInvestorId: ReportsListObj | string | null;
    selectedFromDate: string;
    selectedToDate: string;
    // downloadOption: string;
}

export default function CorporateActionReport({ handleSideSectionDisabled }: { handleSideSectionDisabled: (value: boolean) => void }): JSX.Element {

    const formikRef = useRef<FormikProps<Values>>(null);

    const dispatch = useDispatch()

    const { enqueueSnackbar } = useSnackbar();

    //Data from redux store
    const { iwappRmId = "", investorClientId = "", panNo: currentInvestorPanNo = "", investorName: currentInvestorName = "" } = useSelector((state: RootStateType) => state.id);
    const { isNavbarInfoLoading, businessDate } = useSelector((state: RootStateType) => state.navbarInfo);
    const { role = '' } = useSelector((state: RootStateType) => state.auth);
    const { selectedPortfolioObj } = useSelector((state: RootStateType) => state.selectedPortfiolio)

    // State
    const [isLoading, setLoading] = useState<boolean>(false)
    const [investorList, setInvestorList] = useState<Array<ReportsListObj>>([])
    const [isDownloading, setDownloading] = useState<boolean>(false)
    const [isNoInvestorDialogVisible, setNoInvestorDialogVisible] = useState<boolean>(false)

    const isInvestor = isModuleAllowed({ value: role, compareArr: [USER_ROLES.INVESTOR] })

    const fetchInvestorList = async () => {
        try {
            setLoading(true);

            let response = {} as ReportsListRes

            if ([USER_ROLES.ANALYST].includes(role)) {
                response = await (dispatch(getReportsAnalystInvestorList({
                    analystId: `${iwappRmId}`
                })) as unknown as Promise<ReportsListRes>);
            } else {
                response = await (dispatch(getReportsInvestorList({
                    rmId: `${iwappRmId}`,
                    reportName: "corporate-action-report"
                })) as unknown as Promise<ReportsListRes>);
            }

            if (response?.isSuccess) {
                setInvestorList(response?.result ?? []);
            } else if (!response?.isSuccess) {
                setNoInvestorDialogVisible(true)
            }
        } catch (e) {
            console.log((e as Error).message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isInvestor) {
            fetchInvestorList();
        }
    }, [])

    const todayDate = new Date();
    const formattedBusinessDate = businessDate?.length ? dayjs(businessDate, "DD/MM/YYYY").format("YYYY-MM-DD") : dayjs(todayDate).format("YYYY-MM-DD");

    const initialValues: Values = {
        selectedInvestorId: isInvestor ? investorClientId : null,
        selectedFromDate: formattedBusinessDate,
        selectedToDate: formattedBusinessDate,
        // downloadOption: ''
    }

    const validationSchema = yup.object().shape({
        selectedInvestorId: isInvestor ?
            yup
                .string()
                .nullable() :
            yup
                .object()
                .nullable()
                .test(
                    "investor-required",
                    "Please select an investor",
                    function (value) { return !!value }
                ),
        selectedFromDate: yup
            .date()
            .required('From Date is required')
            .typeError('From Date is required')

            // .min(new Date("1950-01-01"), "From Date should not be less than 01/01/1950")
            .test('min-date', "From Date should not be less than 01/01/1950", function (value) {
                if (!value) return true;
                return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
            })

            // .max(formattedBusinessDate, "From Date should not be greater than system's date")
            .test('max-today', "From Date should not be greater than system's date", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrBefore(dayjs(formattedBusinessDate), 'day');
            })

            .test('fromDateTest', 'From Date cannot be greater than To Date', function (value) {
                // const { selectedToDate } = this.parent;
                // return !selectedToDate || !value || value <= selectedToDate;

                const { selectedToDate } = this.parent;
                if (!selectedToDate || !value) return true;
                return dayjs(value).isSameOrBefore(dayjs(selectedToDate), 'day');
            }),
        selectedToDate: yup
            .date()
            .required('To Date is required')
            .typeError('To Date is required')

            // .min(new Date("1950-01-01"), "To Date should not be less than 01/01/1950")
            .test('min-date', "To Date should not be less than 01/01/1950", function (value) {
                if (!value) return true;
                return dayjs(value).isAfter(dayjs('1950-01-01'), 'day');
            })

            // .max(formattedBusinessDate, "To Date should not be greater than system's date")
            .test('max-today', "To Date should not be greater than system's date", function (value) {
                if (!value) return true;
                return dayjs(value).isSameOrBefore(dayjs(formattedBusinessDate), 'day');
            })

            .test('toDateTest', 'To Date cannot be less than From Date', function (value) {
                // const { selectedFromDate } = this.parent;
                // return !selectedFromDate || !value || value >= selectedFromDate;

                const { selectedFromDate } = this.parent;
                if (!selectedFromDate || !value) return true;
                return dayjs(value).isSameOrAfter(dayjs(selectedFromDate), 'day');
            }),
        // downloadOption: yup.string().required('Please select download option'),
    })

    const handleSubmit = async ({
        selectedInvestorId,
        selectedFromDate,
        selectedToDate,
        // downloadOption
    }: Values) => {
        try {
            setDownloading(true)
            handleSideSectionDisabled(true)

            const payload = {
                portfolioId: isInvestor ? `${selectedPortfolioObj?.portfolioId ?? ""}` : `${(selectedInvestorId as ReportsListObj)?.portfolioList?.[0]?.portfolioId ?? ""}`,
                start: dayjs(selectedFromDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
                end: dayjs(selectedToDate, "YYYY-MM-DD").format("DD/MM/YYYY"),
                clientCode: isInvestor ? `${selectedInvestorId ?? ""}` : `${(selectedInvestorId as ReportsListObj)?.clientCode ?? ""}`,
                // isPDF: (`${downloadOption ?? ""}`.toLowerCase()) === 'pdf',
                isPDF: false,
                investorName: isInvestor ? (currentInvestorName ?? "") : ((selectedInvestorId as ReportsListObj)?.customerName ?? ''),
                panNo: isInvestor ? (currentInvestorPanNo ?? "") : ((selectedInvestorId as ReportsListObj)?.panNo ?? '')
            }

            const response = await (dispatch(getCorporateActionReport(payload)) as unknown as Promise<any>)

            if ([CONTENT_TYPE.PDF, CONTENT_TYPE.EXCEL].includes(response.type)) {
                enqueueSnackbar('File downloaded successfully.', {
                    variant: 'success',
                    autoHideDuration: 2000,
                });

                reportDownload(response as unknown as Blob, `CorporateActionReport.${response.type === CONTENT_TYPE.PDF ? 'pdf' : 'xlsx'}`);
            } else {
                enqueueSnackbar('File download failed!!', {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            }

        } catch (error) {
            console.log("Error: ", error)
        } finally {
            setDownloading(false)
            handleSideSectionDisabled(false)
        }
    }

    const handleNoInvestorDialogClose = () => {
        setNoInvestorDialogVisible(false)
    }

    const handleInvestorChange = (selectedObj: Record<string, any>) => {
        if (!formikRef.current) return

        const { values, setValues } = formikRef.current ?? {}

        if (!selectedObj) {
            setValues({
                ...values,
                selectedInvestorId: null,
                selectedFromDate: formattedBusinessDate,
                selectedToDate: formattedBusinessDate,
                // downloadOption: ''
            })

            return
        }

        setValues({
            ...values,
            selectedInvestorId: selectedObj as ReportsListObj,
            selectedFromDate: formattedBusinessDate,
            selectedToDate: formattedBusinessDate,
            // downloadOption: ''
        })
    }


    return (
        <>
            <ReportsRightLayout>
                <Typography
                    variant='h2'
                    sx={{
                        color: 'text.primary',
                        lineHeight: '25px',
                        letterSpacing: '0.01px',
                        mb: '40px'
                    }}>
                    Corporate Action Report
                </Typography>
                <Formik
                    innerRef={formikRef}
                    validationSchema={validationSchema}
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ values }) => (
                        <Form>
                            <Grid container columnSpacing={3} rowSpacing={1.5} >
                                {!isInvestor && (
                                    <Grid item xs={12} lg={6}>
                                        {(isLoading || isNavbarInfoLoading) && (
                                            <Skeleton
                                                animation={'wave'}
                                                height={90}
                                            />
                                        )}
                                        {(!isLoading && !isNavbarInfoLoading) && (
                                            <FormikSearchDropdown
                                                name='selectedInvestorId'
                                                label='Select Investor *'
                                                options={(investorList ?? [])?.map((obj: ReportsListObj) => ({
                                                    ...obj,
                                                    label: `${maskInfo(obj.customerName)} (${obj?.clientCode ?? ""}) ${obj?.investorType ? "(" + obj?.investorType + ")" : ""} ${`${obj?.isFamilyHead ?? ""}`.toLowerCase() === "y" ? "(Head Of Family)" : ""} ${MPOWER_ACCOUNT_TYPE_MASTER[obj?.accountType ?? ""] ? " - " + MPOWER_ACCOUNT_TYPE_MASTER[obj.accountType] : ""}`,
                                                    value: obj.clientCode
                                                }))}
                                                onChange={handleInvestorChange}
                                                disabled={isLoading || isDownloading || isNavbarInfoLoading}
                                            />
                                        )}
                                    </Grid>
                                )}
                                <Grid item xs={12} lg={6}>
                                    {(isLoading || isNavbarInfoLoading) && (
                                        <Skeleton
                                            animation={'wave'}
                                            height={90}
                                        />
                                    )}
                                    {(!isLoading && !isNavbarInfoLoading) && (
                                        <DayJsDatePicker
                                            name='selectedFromDate'
                                            label="From Date *"
                                            maxDate={new Date(formattedBusinessDate)}
                                            disabled={isLoading || isDownloading || isNavbarInfoLoading}
                                            currentDate={formattedBusinessDate}
                                            resetDate
                                        />
                                    )}
                                </Grid>
                                <Grid item xs={12} lg={6}>
                                    {(isLoading || isNavbarInfoLoading) && (
                                        <Skeleton
                                            animation={'wave'}
                                            height={90}
                                        />
                                    )}
                                    {(!isLoading && !isNavbarInfoLoading) && (
                                        <DayJsDatePicker
                                            name='selectedToDate'
                                            label="To Date *"
                                            maxDate={new Date(formattedBusinessDate)}
                                            minDate={new Date(values.selectedFromDate)}
                                            disabled={isLoading || isDownloading || isNavbarInfoLoading}
                                            currentDate={formattedBusinessDate}
                                            resetDate
                                        />
                                    )}
                                </Grid>
                                {/* <Grid item xs={12} lg={6}>
                                    {(isLoading || isNavbarInfoLoading) && (
                                        <Skeleton
                                            animation={'wave'}
                                            height={90}
                                        />
                                    )}
                                    {(!isLoading && !isNavbarInfoLoading) && (
                                        <MFSelectField
                                            name='downloadOption'
                                            label="Select Download Option *"
                                            items={REPORTS_DOWNLOAD_OPTION}
                                            disabled={isDownloading}
                                        />
                                    )}
                                </Grid> */}
                                <Grid item container columnSpacing={3} justifyContent="flex-end" marginTop={1}>
                                    <Grid item xs={12} lg={6}>
                                        {(isLoading || isNavbarInfoLoading) && (
                                            <Skeleton
                                                animation={'wave'}
                                                height={90}
                                            />
                                        )}
                                        {(!isLoading && !isNavbarInfoLoading) && (
                                            <MFSubmitButton
                                                label="Download"
                                                loadingEnable={isDownloading}
                                                disabled={isLoading}
                                            />
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </ReportsRightLayout>
            <Dialog
                onClose={handleNoInvestorDialogClose}
                open={isNoInvestorDialogVisible}
                maxWidth='sm'
                sx={(theme: Theme) => ({
                    '& .MuiPaper-root': {
                        borderRadius: '12px',
                        backgroundColor: theme.palette.common.white,
                        padding: "22px 24px",
                        width: "533px",
                    },
                })}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        rowGap: "8px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                columnGap: "12px",
                                alignItems: "center"
                            }}
                        >
                            <YellowWarning />
                            <Typography
                                variant="h2"
                                sx={(theme: Theme) => ({ color: theme.palette.text.primary })}
                            >
                                No Investor Found!!
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="close"
                            onClick={handleNoInvestorDialogClose}
                            sx={{
                                padding: "5px"
                            }}
                        >
                            <DialogCloseIcon />
                        </IconButton>
                    </Box>
                </Box>
            </Dialog>
        </>
    )
}

